import { createAuth0Client } from '@auth0/auth0-spa-js';

class AuthService {
  constructor() {
    this.domain = process.env.REACT_APP_AUTH0_DOMAIN || 'krosskast-master-account.us.auth0.com';
    this.clientId = process.env.REACT_APP_AUTH0_CLIENT_ID || 'qF6uhDsfIapcwJIHK7EKXa2qJY70lQv6';
    this.audience = process.env.REACT_APP_AUTH0_AUDIENCE || 'https://api.krosskast.com';
    this.scope = process.env.REACT_APP_AUTH0_SCOPE || 'openid profile email offline_access';
    this.redirectUri = `${window.location.origin}/callback`;
    this.auth0Client = null;

    this.accessToken = localStorage.getItem('auth_token');
    this.user = JSON.parse(localStorage.getItem('auth_user') || 'null');
    this.expiresAt = Number(localStorage.getItem('auth_expires_at') || 0);
    this.isAuthenticated = this.isLoggedIn();
  }

  async initialize() {
    if (!this.auth0Client) {
      this.auth0Client = await createAuth0Client({
        domain: this.domain,
        clientId: this.clientId,
        authorizationParams: {
          redirect_uri: this.redirectUri,
          audience: this.audience,
          scope: this.scope,
        },
        cacheLocation: 'localstorage',
        useRefreshTokens: true,
      });
    }
    return this.auth0Client;
  }

  decodeTokenExpiry(token) {
    try {
      const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
      if (payload?.exp) {
        return payload.exp * 1000;
      }
    } catch (error) {
      console.warn('Unable to decode token expiry, using fallback:', error);
    }

    return Date.now() + 55 * 60 * 1000;
  }

  persistSession(token, user) {
    const expiresAt = this.decodeTokenExpiry(token);
    this.accessToken = token;
    this.user = user;
    this.expiresAt = expiresAt;
    this.isAuthenticated = true;

    localStorage.setItem('auth_token', token);
    localStorage.setItem('auth_user', JSON.stringify(user || null));
    localStorage.setItem('auth_expires_at', String(expiresAt));

    // Keep profile chrome bound to the authenticated Auth0 user (never a
    // prior session's localStorage userInfo / demo placeholders).
    try {
      const {
        persistUserInfo,
        loadStoredUserInfo,
        syncProfileForAuthenticatedUser,
        clearStoredUserInfo,
      } = require('../auth/profileIdentity');
      if (user?.sub) {
        const stored = loadStoredUserInfo(String(user.sub));
        const synced = syncProfileForAuthenticatedUser(user, stored);
        persistUserInfo(synced, String(user.sub));
      } else {
        clearStoredUserInfo();
      }
      window.dispatchEvent(new Event('kk-auth-session-changed'));
    } catch {
      /* ignore — profile sync is best-effort */
    }
  }

  /**
   * Clear KrossKast MA application session keys only.
   *
   * MUST NOT clear Auth0 SPA `@@auth0spajs@@` cache or null `auth0Client`.
   * `isLoggedIn()` calls this when MA tokens are absent/expired; wiping Auth0
   * cache there destroys tokens just written by handleRedirectCallback and
   * causes a /signin ↔ Auth0 redirect loop.
   */
  clearLocalSession() {
    this.accessToken = null;
    this.user = null;
    this.expiresAt = 0;
    this.isAuthenticated = false;

    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_refresh_token');
    localStorage.removeItem('auth_expires_at');
    localStorage.removeItem('auth_user');
    try {
      // Prevent cross-user MA bootstrap using a prior owner's stored id.
      localStorage.removeItem('kk_active_master_account_id');
      // Prevent prior-user profile chrome (name/email/avatar) surviving logout.
      localStorage.removeItem('userInfo');
      // Prevent prior Brand chrome surviving logout / next user session.
      localStorage.removeItem('currentBrand');
      window.dispatchEvent(new Event('kk-auth-session-changed'));
    } catch {
      /* ignore */
    }
  }

  /**
   * Intentional identity invalidation (logout, password-changed fresh login):
   * clearLocalSession() plus the pre-login return-to, so a stale Brand-scoped
   * route cannot be resumed after the Brand context has been wiped.
   *
   * Not part of clearLocalSession(): isLoggedIn() runs that between
   * ProtectedRoute stashing a deep link and /signin, which must keep it.
   */
  invalidateLocalSession() {
    this.clearLocalSession();
    try {
      sessionStorage.removeItem('kk_post_login_return_to');
    } catch {
      /* ignore */
    }
  }

  /**
   * Intentional post-password-reset invalidation: drop prior MA identity AND
   * Auth0 SPA token cache so User A cannot be silently restored. Call only
   * before starting a new loginWithRedirect — never during /callback.
   */
  invalidatePriorIdentityForFreshLogin() {
    const {
      clearAuth0SpaLocalCache,
      markRequireFreshLogin,
    } = require('../auth/passwordChangedSession');
    this.invalidateLocalSession();
    clearAuth0SpaLocalCache(localStorage);
    this.auth0Client = null;
    markRequireFreshLogin();
  }

  async handleRedirectCallback() {
    try {
      const {
        consumeRequireFreshLogin,
        shouldConsumeFreshLoginGate,
      } = require('../auth/passwordChangedSession');

      await this.initialize();
      const callback = await this.auth0Client.handleRedirectCallback();
      // Deep link carried by THIS Auth0 transaction only (see loginWithRedirect).
      const returnTo = callback?.appState?.returnTo || null;

      const user = await this.auth0Client.getUser();
      // Read tokens from the Auth0 client that just completed the code exchange.
      // Do not route through isLoggedIn() → clearLocalSession() first.
      const token = await this.auth0Client.getTokenSilently({
        authorizationParams: {
          audience: this.audience,
          scope: this.scope,
        },
      });
      this.persistSession(token, user);

      if (shouldConsumeFreshLoginGate({ callbackSucceeded: true })) {
        consumeRequireFreshLogin();
      }

      return { success: true, user: this.user, returnTo };
    } catch (error) {
      console.error('AuthService: Callback error:', error);
      // Keep require-fresh-login gate so retry still uses prompt=login.
      return { success: false, error: error.message };
    }
  }

  async login(email, password) {
    void password;
    try {
      await this.initialize();

      await this.auth0Client.loginWithRedirect({
        authorizationParams: {
          redirect_uri: this.redirectUri,
          audience: this.audience,
          scope: this.scope,
          login_hint: email,
        },
      });

      return { success: true };
    } catch (error) {
      console.error('AuthService: Login error:', error);
      return { success: false, error: error.message };
    }
  }

  async loginWithRedirect(options = {}) {
    try {
      await this.initialize();

      const authorizationParams = {
        redirect_uri: this.redirectUri,
        audience: this.audience,
        scope: this.scope,
      };
      // Auth0-supported re-auth: do not silently restore a prior SSO user
      // after password-reset completion (or when callers pass prompt).
      if (options.prompt) {
        authorizationParams.prompt = options.prompt;
      }

      // A post-login deep link rides on this one Auth0 transaction (the SDK
      // keeps a single transaction per tab; a new attempt overwrites it).
      await this.auth0Client.loginWithRedirect({
        authorizationParams,
        ...(options.returnTo ? { appState: { returnTo: options.returnTo } } : {}),
      });

      return { success: true };
    } catch (error) {
      console.error('AuthService: LoginWithRedirect error:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * After Auth0 password-change lands on /password-changed: wipe any prior
   * MA SPA identity and start a forced interactive login. Never navigate to
   * /app via a residual isLoggedIn() session.
   */
  async continueAfterPasswordChange() {
    try {
      this.invalidatePriorIdentityForFreshLogin();
      return await this.loginWithRedirect({ prompt: 'login' });
    } catch (error) {
      console.error('AuthService: continueAfterPasswordChange error:', error);
      // Fail closed: Auth0 logout clears SSO, then /signin starts UL login.
      // Gate remains marked so /signin still uses prompt=login.
      try {
        const {
          markRequireFreshLogin,
        } = require('../auth/passwordChangedSession');
        markRequireFreshLogin();
        await this.logout();
      } catch {
        window.location.assign('/signin');
      }
      return { success: false, error: error?.message || String(error) };
    }
  }

  async signup(email) {
    try {
      await this.initialize();

      await this.auth0Client.loginWithRedirect({
        authorizationParams: {
          redirect_uri: this.redirectUri,
          audience: this.audience,
          scope: this.scope,
          login_hint: email,
          screen_hint: 'signup',
        },
      });

      return { success: true };
    } catch (error) {
      console.error('AuthService: Signup error:', error);
      return { success: false, error: error.message };
    }
  }

  async getUserInfo() {
    const token = await this.getValidAccessToken();
    if (!token) return null;

    try {
      const response = await fetch(`https://${this.domain}/userinfo`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        this.user = await response.json();
        localStorage.setItem('auth_user', JSON.stringify(this.user));
        return this.user;
      }
    } catch (error) {
      console.error('Error getting user info:', error);
    }
    return null;
  }

  isLoggedIn() {
    const token = localStorage.getItem('auth_token');
    const expiresAt = Number(localStorage.getItem('auth_expires_at') || 0);

    if (token && expiresAt && Date.now() < expiresAt) {
      this.accessToken = token;
      this.isAuthenticated = true;
      this.user = JSON.parse(localStorage.getItem('auth_user') || 'null');
      this.expiresAt = expiresAt;
      return true;
    }

    this.clearLocalSession();
    return false;
  }

  getAccessToken() {
    try {
      const { getImpersonationToken } = require('../supportMode/supportModeSession');
      const imp = getImpersonationToken();
      if (imp) return imp;
    } catch (_) {
      /* ignore */
    }
    if (!this.accessToken && this.isLoggedIn()) {
      this.accessToken = localStorage.getItem('auth_token');
    }
    return this.accessToken;
  }

  getCurrentUser() {
    if (!this.user && this.isLoggedIn()) {
      this.user = JSON.parse(localStorage.getItem('auth_user') || 'null');
    }
    return this.user;
  }

  getUser() {
    return this.getCurrentUser();
  }

  async getValidAccessToken(forceRefresh = false) {
    try {
      const { getImpersonationToken } = require('../supportMode/supportModeSession');
      const imp = getImpersonationToken();
      if (imp) return imp;
    } catch (_) {
      /* ignore */
    }

    if (!forceRefresh && this.isLoggedIn() && this.accessToken) {
      return this.accessToken;
    }

    await this.initialize();

    const tokenOptions = {
      authorizationParams: {
        audience: this.audience,
        scope: this.scope,
      },
    };

    if (forceRefresh) {
      tokenOptions.cacheMode = 'off';
      tokenOptions.ignoreCache = true;
    }

    const token = await this.auth0Client.getTokenSilently(tokenOptions);
    const user = this.user || (await this.auth0Client.getUser());
    this.persistSession(token, user);

    return token;
  }

  async logout() {
    // Clear MA-local identity/tenant state BEFORE Auth0 redirects away,
    // otherwise clearLocalSession after logout() never runs.
    this.invalidateLocalSession();
    try {
      sessionStorage.removeItem('justLoggedOut');
      await this.initialize();
      const { buildPostLogoutReturnTo } = require('../auth/logoutFlow');
      await this.auth0Client.logout({
        logoutParams: {
          // Allowed Logout URL on staging SPA — /signin immediately starts
          // branded Auth0 Universal Login (no unbranded intermediate page).
          returnTo: buildPostLogoutReturnTo(window.location.origin),
        },
      });
    } catch (error) {
      console.error('Logout error:', error);
      // Fallback: still send the browser to the MA login entrypoint.
      try {
        const { MA_POST_LOGOUT_PATH } = require('../auth/logoutFlow');
        window.location.assign(MA_POST_LOGOUT_PATH);
      } catch {
        window.location.assign('/signin');
      }
    }
  }

  async refreshToken() {
    try {
      await this.getValidAccessToken(true);
      return true;
    } catch (error) {
      console.error('Token refresh error:', error);
      await this.logout();
      return false;
    }
  }
}

export default new AuthService();
